<template>
  <div
    class="custom-box-shadow flex h-full w-auto flex-col place-content-between gap-4 rounded-xl border border-light-gray p-4 md:w-full lg:rounded"
  >
    <div class="mr-2 flex flex-row justify-between">
      <div class="flex flex-col">
        <h1 class="mr-4 text-lg font-medium">
          {{ this.insurance_types[policy?.is_type] }}
        </h1>
        <p
          class="text-info-subtle-text mb-4 mt-1 whitespace-nowrap text-xs uppercase"
        >
          {{ subheading }}
        </p>
      </div>
      <custom-badge
        v-if="policy?.status === 'inactive'"
        text="Cancelled"
        size="md"
        class="h-7"
        bgColorClass="bg-orange-500"
        colorClass="text-white"
      ></custom-badge>
      <custom-badge
        v-else-if="policy?.cancellation_date"
        :text="'Pending Cancellation'"
        size="md"
        class="h-7"
        bgColorClass="bg-orange-500"
        colorClass="text-white"
      >
      </custom-badge>
      <custom-badge
        v-else-if="policy?.status === 'active'"
        :text="'Active'"
        size="md"
        class="h-7"
        bgColorClass="bg-green-500"
        colorClass="text-white"
      ></custom-badge>
      <custom-badge
        v-else
        :text="getActiveInDays()"
        size="md"
        class="h-7"
        bgColorClass="bg-light-gray text-body-color"
        colorClass="text-white"
      ></custom-badge>
    </div>
    <div class="flex flex-col">
      <span class="text-info-subtle-text text-xs font-semibold"
        >Insurer Name</span
      >
      <p class="text-info-bold-text text-base font-normal">
        {{ nullTextify(policy?.insurance_insurer?.name) }}
      </p>
    </div>
    <div class="flex justify-start gap-12">
      <div class="flex flex-col gap-6">
        <div class="flex flex-col">
          <span class="text-info-subtle-text mb-1 text-xs font-semibold"
            >Policy Number</span
          >
          <span class="text-info-bold-text text-base font-normal">{{
            formatPolicy(policy?.number)
          }}</span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-col gap-6"
      v-if="policy?.cancellation_date"
    >
      <div class="align-items flex justify-between">
        <div class="flex flex-col">
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >Eff. Date</span
          >
          <span class="text-info-bold-text text-base font-normal">
            {{ calculateExpirationDate(policy?.effective_date || null) }}
          </span>
        </div>
        <div class="flex flex-col">
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >Exp. Date</span
          >
          <span
            class="text-base font-normal"
            :class="
              isExpirationWithin7Days ? 'text-red-500' : 'text-info-bold-text'
            "
          >
            {{ calculateExpirationDate(policy?.expiration_date || null) }}
          </span>
        </div>
        <div class="flex flex-col">
          <div class="flex">
            <span
              class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
              >Cancel. Date</span
            >
            <div style="margin-top: -6px; margin-left: 2px">
              <info-tooltip
                colorScheme="dark"
                content=""
                class="relative"
                style="top: 1.5px"
                size="xs"
                contentWidthClass="w-64"
              >
                <div v-if="policy.status === 'inactive'">
                  A cancellation notice has been provided or loss of coverage
                  has been detected.
                </div>
                <div v-else>
                  A notice has been provided stating the policy may be cancelled
                  on this date.
                </div>
              </info-tooltip>
            </div>
          </div>
          <span class="text-info-bold-text text-base font-normal">
            {{ calculateExpirationDate(policy?.cancellation_date || null) }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-col gap-6"
      v-else
    >
      <div class="align-items flex">
        <div class="mr-5 flex flex-col">
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >Effective Date</span
          >
          <span class="text-info-bold-text text-base font-normal">
            {{ calculateExpirationDate(policy?.effective_date || null) }}
          </span>
        </div>
        <div class="flex flex-col">
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >Expiration Date</span
          >
          <span
            class="text-base font-normal"
            :class="
              isExpirationWithin7Days ? 'text-red-500' : 'text-info-bold-text'
            "
          >
            {{ calculateExpirationDate(policy?.expiration_date || null) }}
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-6">
      <div class="flex gap-2">
        <div
          v-if="
            policy.is_type !== 'commercial_general_liability' &&
            policy.is_type !== 'umbrella_liability' &&
            policy.is_type !== 'excess_liability' &&
            policy.is_type !== 'employers_liability'
          "
          class="flex flex-col"
        >
          <span class="text-info-subtle-text mb-1 text-xs font-semibold"
            >Limit</span
          >
          <span
            v-if="!isWorkerCompensation(policy?.is_type)"
            class="text-info-bold-text text-base font-normal"
          >
            {{ calculateLimit(policy?.limit || null) }}
            {{ calculateCurrency(policy?.insurance_certificate?.currency) }}
          </span>
          <span
            v-else
            class="text-info-bold-text text-base font-normal"
          >
            {{ hasStatutoryLimit(policy) ? 'Statutory' : 'Other' }}
          </span>
        </div>
        <div
          v-if="
            policy.is_type === 'umbrella_liability' ||
            policy.is_type === 'excess_liability' ||
            policy.is_type === 'commercial_general_liability'
          "
          class="flex flex-col"
        >
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >Each Ocurrence</span
          >
          <span class="text-info-bold-text text-base font-normal">{{
            Math.abs(eachOcurrenceLimit(policy)?.value) > 0
              ? calculateLimit(Math.abs(eachOcurrenceLimit(policy).value))
              : '-'
          }}</span>
        </div>
        <div
          v-if="
            policy.is_type === 'umbrella_liability' ||
            policy.is_type === 'excess_liability' ||
            policy.is_type === 'commercial_general_liability'
          "
          class="flex flex-col"
        >
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >General Aggregate</span
          >
          <span class="text-info-bold-text text-base font-normal">{{
            Math.abs(aggregateLimit(policy)?.value) > 0
              ? calculateLimit(Math.abs(aggregateLimit(policy).value))
              : '-'
          }}</span>
        </div>
        <div
          v-if="policy.is_type === 'employers_liability'"
          class="flex flex-col"
        >
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >E.L. Each Accident</span
          >
          <span class="text-info-bold-text text-base font-normal">{{
            Math.abs(elEachAccidentLimit(policy)?.value) > 0
              ? calculateLimit(Math.abs(elEachAccidentLimit(policy).value))
              : '-'
          }}</span>
        </div>
        <div
          v-if="policy.is_type === 'employers_liability'"
          class="flex flex-col"
        >
          <span
            class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
            >E.L. Disease - EA Employee</span
          >
          <span class="text-info-bold-text text-base font-normal">{{
            Math.abs(elDiseaseEachEmployeeLimit(policy)?.value) > 0
              ? calculateLimit(
                  Math.abs(elDiseaseEachEmployeeLimit(policy).value)
                )
              : '-'
          }}</span>
        </div>
      </div>
      <div
        v-if="policy.is_type === 'employers_liability'"
        class="flex flex-col"
      >
        <span
          class="text-info-subtle-text mb-1 whitespace-nowrap text-xs font-semibold"
          >E.L. Disease - Limit</span
        >
        <span class="text-info-bold-text text-base font-normal">{{
          Math.abs(elDiseaseLimit(policy)?.value) > 0
            ? calculateLimit(Math.abs(elDiseaseLimit(policy).value))
            : '-'
        }}</span>
      </div>
    </div>
    <div class="justify-self-end">
      <button
        class="text-info-bold-text mt-1 h-12 w-full cursor-pointer rounded-xl p-1 text-base font-medium hover:opacity-80 lg:h-10 lg:rounded-md"
        style="background-color: rgba(55, 64, 83, 0.1)"
        @click="
          showCertificateAsPDF(
            policy?.insurance_certificate?.certificate_image?.url
          )
        "
      >
        View Certificate #{{ policy?.insurance_certificate_id || 'N/A' }}
      </button>
    </div>
  </div>
</template>
<script>
import CustomBadge from '@/components/CustomBadge.vue';
import moment from 'moment';
import InfoTooltip from '@/components/InfoTooltip.vue';

export default {
  components: { CustomBadge, InfoTooltip },
  name: 'InsuranceCard',
  props: {
    policy: {
      type: Object,
      required: true,
    },
    increaseTheHeight: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      insurance_types: {
        commercial_general_liability: 'General Liability',
        automobile_liability: 'Auto',
        motor_truck_cargo: 'Cargo',
        workers_compensation: "Workers' Compensation",
        trailer_interchange: 'Trailer Interchange',
        contingent_auto: 'Contingent Auto',
        contingent_cargo: 'Contingent Cargo',
        umbrella_liability: 'Umbrella Liability',
        excess_liability: 'Excess Liability',
        employers_liability: 'Employers Liability',

        // refrigeration_breakdown: "Reefer Breakdown", // TODO: We are ignoring refrigeration_breakdown for now. We are currently only showing the types for Auto and Cargo.
      },
    };
  },
  computed: {
    isExpirationWithin7Days() {
      const today = new Date();
      const expiration = new Date(this.policy?.expiration_date);
      const timeDiff = expiration.getTime() - today.getTime();
      const diffInDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

      if (diffInDays <= 7 && this.policy.status === 'active') {
        // If expiration is within 7 days and status is active, put the date in red font
        return true;
      } else {
        return false;
      }
    },
    subheading() {
      if (!this.policy?.is_type) return '';

      if (this.policy?.scheduled_autos) {
        return 'scheduled autos';
      }
      if (this.policy?.is_refrigeration_breakdown_included) {
        return 'includes reefer breakdown';
      }

      return '';
    },
  },
  methods: {
    elEachAccidentLimit(policy) {
      return policy?.insurance_limits?.find(
        (limit) => limit?.is_type === 'el_each_accident'
      );
    },
    elDiseaseEachEmployeeLimit(policy) {
      return policy?.insurance_limits?.find(
        (limit) => limit?.is_type === 'el_disease_each_employee'
      );
    },
    elDiseaseLimit(policy) {
      return policy?.insurance_limits?.find(
        (limit) => limit?.is_type === 'disease_policy_limit'
      );
    },
    eachOcurrenceLimit(policy) {
      return policy?.insurance_limits?.find(
        (limit) => limit?.is_type === 'each_occurrence'
      );
    },
    aggregateLimit(policy) {
      if (
        policy.is_type === 'umbrella_liability' ||
        policy.is_type === 'excess_liability'
      ) {
        return policy?.insurance_limits?.find(
          (limit) => limit?.is_type === 'aggregate'
        );
      }

      return policy?.insurance_limits?.find(
        (limit) => limit?.is_type === 'general_aggregate'
      );
    },
    getActiveInDays() {
      const today = new Date();
      const effectiveDate = new Date(this.policy?.effective_date);

      const millisecondsInADay = 1000 * 60 * 60 * 24;
      const differenceInMilliseconds = effectiveDate - today;
      const daysUntilActive = Math.ceil(
        differenceInMilliseconds / millisecondsInADay
      );

      if (daysUntilActive > 0) {
        return `Active in ${daysUntilActive} Days`;
      }
    },
    formatPolicy(number) {
      const policy = this.nullTextify(number);

      if (policy?.length > 30) {
        return `${policy.slice(0, 28)}...`;
      }

      return policy;
    },
    isWorkerCompensation(policyType) {
      return policyType === 'workers_compensation';
    },
    hasStatutoryLimit({ statutory_limits }) {
      return !!statutory_limits;
    },
    displayDate: function (initialDate) {
      if (initialDate == null) {
        return initialDate;
      } else {
        return moment(initialDate, 'YYYY-MM-DD').format('M/D/YY');
      }
    },
    nullTextify: function (value, nullVal = 'N/A') {
      if (value == null) {
        return nullVal;
      } else {
        return value;
      }
    },
    calculateLimit(limit) {
      if (limit) {
        return '$' + Math.round(limit)?.toLocaleString();
      } else {
        return '-';
      }
    },
    calculateExpirationDate(expiration) {
      if (expiration) {
        return moment(expiration).format('M/D/YY');
      } else {
        return '';
      }
    },
    calculateCurrency(currency) {
      if (currency !== 'usd') {
        return currency?.toUpperCase();
      } else {
        return '';
      }
    },
    showCertificateAsPDF(url) {
      window.open(url);
    },
  },
};
</script>
